import { combineReducers, type Reducer } from '@reduxjs/toolkit';

import { adminApi } from './Admin/AdminService';
import { alertApi } from './Alert/AlertService';
import { authServiceApi } from './Auth/AuthService';
import { authReducer } from './Auth/authSlice';
import { brokerApi } from './Broker/BrokerService';
import { compareReducer } from './Compare/compareSlice';
import { buyerApi } from './Buyer/BuyerService';
import { buyerEnquiryApi } from './BuyerEnquiry/BuyerEnquiryService';
import { lawyerApi } from './Lawyer/LawyerService';
import { legalRequestApi } from './LegalRequest/LegalRequestService';
import { negotiationApi } from './Negotiation/NegotiationService';
import { notificationApi } from './Notifications/NotificationsService';
import { ownershipApi } from './Ownership/OwnershipService';
import { OWNERSHIP_UI_KEY, ownershipUiReducer } from './Ownership/ownershipUiSlice';
import { paymentApi } from './Payment/PaymentService';
import { postalServiceApi } from './Postal/PostalService';
import { propertyApi } from './Property/PropertyService';
import { publicApi } from './Public/PublicService';
import { sellerApi } from './Seller/SellerService';
import { settingsReducer } from './Settings/settingsSlice';

/**
 * Every API slice is keyed by its own `reducerPath`. A hand-typed key that disagrees with
 * `reducerPath` compiles and then fails at the first query with "Could not find the slice
 * reducer" — so always spread the constant, never write the string.
 *
 * The list is written out rather than derived from `apiSlices`: a mapped object widens to
 * `Record<string, Reducer>`, which erases the per-slice state types and breaks
 * `configureStore`'s middleware inference. Keeping both lists in step is the cost; the
 * `apiSlices` comment says why that list exists.
 */
export const rootReducer = combineReducers({
  auth: authReducer,
  settings: settingsReducer,
  compare: compareReducer,
  [OWNERSHIP_UI_KEY]: ownershipUiReducer,
  [authServiceApi.reducerPath]: authServiceApi.reducer as Reducer,
  [postalServiceApi.reducerPath]: postalServiceApi.reducer as Reducer,
  [publicApi.reducerPath]: publicApi.reducer as Reducer,
  [propertyApi.reducerPath]: propertyApi.reducer as Reducer,
  [sellerApi.reducerPath]: sellerApi.reducer as Reducer,
  [buyerApi.reducerPath]: buyerApi.reducer as Reducer,
  [alertApi.reducerPath]: alertApi.reducer as Reducer,
  [buyerEnquiryApi.reducerPath]: buyerEnquiryApi.reducer as Reducer,
  [negotiationApi.reducerPath]: negotiationApi.reducer as Reducer,
  [brokerApi.reducerPath]: brokerApi.reducer as Reducer,
  [lawyerApi.reducerPath]: lawyerApi.reducer as Reducer,
  [ownershipApi.reducerPath]: ownershipApi.reducer as Reducer,
  [adminApi.reducerPath]: adminApi.reducer as Reducer,
  [paymentApi.reducerPath]: paymentApi.reducer as Reducer,
  [legalRequestApi.reducerPath]: legalRequestApi.reducer as Reducer,
  [notificationApi.reducerPath]: notificationApi.reducer as Reducer
});

export type RootState = ReturnType<typeof rootReducer>;
